// src/components/sections/Services.tsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  Palette, 
  Code2, 
  Search, 
  Smartphone, 
  Zap, 
  HeartHandshake 
} from 'lucide-react';
import './Services.css';

interface Service {
  icon: React.ReactNode;
  titleKey: string;
  descriptionKey: string;
  gradient: string;
}

const Services: React.FC = () => {
  const { t } = useTranslation();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const services: Service[] = [
    {
      icon: <Palette size={40} />,
      titleKey: 'services.design.title',
      descriptionKey: 'services.design.description',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    },
    {
      icon: <Code2 size={40} />,
      titleKey: 'services.development.title',
      descriptionKey: 'services.development.description',
      gradient: 'linear-gradient(135deg, #00f2fe 0%, #4facfe 100%)'
    },
    {
      icon: <Search size={40} />,
      titleKey: 'services.seo.title',
      descriptionKey: 'services.seo.description',
      gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)'
    },
    {
      icon: <Smartphone size={40} />,
      titleKey: 'services.responsive.title',
      descriptionKey: 'services.responsive.description',
      gradient: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)'
    },
    {
      icon: <Zap size={40} />,
      titleKey: 'services.performance.title',
      descriptionKey: 'services.performance.description',
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
    },
    {
      icon: <HeartHandshake size={40} />,
      titleKey: 'services.support.title',
      descriptionKey: 'services.support.description',
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  return (
    <section id="services" className="services" ref={ref}>
      <div className="container">
        <motion.div 
          className="section-header"
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <h2 className="section-title">{t('services.title')}</h2>
          <p className="section-subtitle">{t('services.subtitle')}</p>
        </motion.div>

        <motion.div 
          className="services-grid"
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          {services.map((service, index) => (
            <motion.div
              key={index}
              className="service-card card"
              variants={itemVariants}
              whileHover={{ 
                y: -10,
                transition: { duration: 0.3 }
              }}
            >
              <div 
                className="service-icon"
                style={{ background: service.gradient }}
              >
                {service.icon}
              </div>
              <h3 className="service-title">{t(service.titleKey)}</h3>
              <p className="service-description">{t(service.descriptionKey)}</p>
              <div className="service-hover-effect" style={{ background: service.gradient }} />
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Services;